import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    TextField,
    FormControlLabel,
    Switch,
    Autocomplete
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import municipalityAPI from "../../../Network/Municipality";
import mizanListAPI from "../../../Network/MizanList";
import Cookies from "universal-cookie";

const cookies = new Cookies();

export default function RenderContent({ formData, setFormData }) {
    const [t] = useTranslation("common");
    const maintheme = useSelector((state) => state.themeData.maintheme);

    // Municipalities state
    const [municipalities, setMunicipalities] = useState([]);
    const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
    const [searchTermMunicipality, setSearchTermMunicipality] = useState("");
    const timerMunicipalities = useRef(null);

    // Geofences state
    const [geofences, setGeofences] = useState([]);
    const [loadingGeofences, setLoadingGeofences] = useState(false);
    const [searchTermGeofence, setSearchTermGeofence] = useState("");
    const timerGeofences = useRef(null);

    const headerObject = {
        username: cookies.get("user_name") || null,
        user_id: cookies.get("user_id") || null,
        account_name: cookies.get("account_name") || null,
        account_id: cookies.get("account_id") || null,
        authHash: cookies.get("authHash") || null,
        sid: cookies.get("sid") || null,
        baseurl: cookies.get("baseUrl") || null,
        id: cookies.get("id") || null,
    };

    // Load municipalities
    const loadMunicipalities = async (search = "") => {
        setLoadingMunicipalities(true);
        const result = await municipalityAPI.getMunicipalities({
            params: {
                ...headerObject,
                name: search || undefined,
                page_size: 20,
                page: 1
            }
        });
        setLoadingMunicipalities(false);
        if (result.status && result?.data?.data?.data) {
            setMunicipalities(result?.data?.data?.data);
        }
    };

    // Load geofences
    const loadGeofences = async (search = "") => {
        setLoadingGeofences(true);
        const result = await mizanListAPI.getGeofences({
            params: {
                ...headerObject,
                name: search || undefined,
                page_size: 20
            }
        });
        setLoadingGeofences(false);
        if (result.status && result?.data?.data) {
            setGeofences(result?.data?.data);
        }
    };

    useEffect(() => {
        loadMunicipalities();
        loadGeofences();
    }, []);

    const handleSearchTermMunicipalityChange = (newValue) => {
        setSearchTermMunicipality(newValue);
        if (timerMunicipalities.current) {
            clearTimeout(timerMunicipalities.current);
        }
        timerMunicipalities.current = setTimeout(() => {
            if (newValue?.length >= 2) {
                loadMunicipalities(newValue);
            } else if (newValue?.length === 0) {
                loadMunicipalities();
            }
        }, 500);
    };

    const handleSearchTermGeofenceChange = (newValue) => {
        setSearchTermGeofence(newValue);
        if (timerGeofences.current) {
            clearTimeout(timerGeofences.current);
        }
        timerGeofences.current = setTimeout(() => {
            if (newValue?.length >= 2) {
                loadGeofences(newValue);
            } else if (newValue?.length === 0) {
                loadGeofences();
            }
        }, 500);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mt: 2
            }}
        >
            <TextField
                label={t("MIZAN_LIST.display_name") || "الاسم"}
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                fullWidth
                required
                InputLabelProps={{ sx: { fontFamily: "Cairo-Medium" } }}
                InputProps={{ sx: { fontFamily: "Cairo-Medium" } }}
            />
            <TextField
                label={t("MIZAN_LIST.code") || "الكود"}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                fullWidth
                InputLabelProps={{ sx: { fontFamily: "Cairo-Medium" } }}
                InputProps={{ sx: { fontFamily: "Cairo-Medium" } }}
            />
            <Autocomplete
                value={formData.municipality_selected}
                options={municipalities}
                loading={loadingMunicipalities}
                loadingText={t('GLOBAL.searching') || 'جاري البحث...'}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => option?.name || ""}
                onInputChange={(event, newInputValue) => {
                    handleSearchTermMunicipalityChange(newInputValue);
                }}
                onChange={(e, v) => {
                    setFormData({
                        ...formData,
                        municipality_id: v?.id || null,
                        municipality_selected: v
                    });
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t("MIZAN_LIST.municipality") || "البلدية"}
                        InputLabelProps={{ sx: { fontFamily: "Cairo-Medium" } }}
                        InputProps={{
                            ...params.InputProps,
                            sx: { fontFamily: "Cairo-Medium" }
                        }}
                    />
                )}
            />
            <Autocomplete
                value={formData.geofence_selected}
                options={geofences}
                loading={loadingGeofences}
                loadingText={t('GLOBAL.searching') || 'جاري البحث...'}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => option?.name || ""}
                onInputChange={(event, newInputValue) => {
                    handleSearchTermGeofenceChange(newInputValue);
                }}
                onChange={(e, v) => {
                    setFormData({
                        ...formData,
                        geofence_id: v?.id || null,
                        geofence_selected: v
                    });
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t("MIZAN_LIST.geofence") || "السياج الجغرافي"}
                        InputLabelProps={{ sx: { fontFamily: "Cairo-Medium" } }}
                        InputProps={{
                            ...params.InputProps,
                            sx: { fontFamily: "Cairo-Medium" }
                        }}
                    />
                )}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={formData.enabled === 1}
                        onChange={(e) => setFormData({ ...formData, enabled: e.target.checked ? 1 : 0 })}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: maintheme?.primary,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: maintheme?.primary,
                            },
                        }}
                    />
                }
                label={t("MIZAN_LIST.enabled") || "مفعل"}
                sx={{ fontFamily: "Cairo-Medium" }}
            />
        </Box>
    );
}
